import React, {useRef} from 'react';
import {SetRequired} from 'type-fest';
import {useConstCallback} from '@netx/core-components/hooks/useConstCallback';
import {
	SelectField,
	type SelectFieldProps,
} from '@netx/core-components/SelectField';
import {splitEventHandler} from '@netx/core-next/utils';
import {combineRefs} from '@netx/core-next/utils/react';
import {useErrorFocus, useFormInput} from '../hooks';
import type {WithLegacyCommonFormFieldProps} from '../types';

export interface LegacyFormSelectFieldProps<
	T,
	Multiple extends boolean = false,
	DisableClearable extends boolean = false,
	FreeSolo extends boolean = false,
> extends WithLegacyCommonFormFieldProps<
		SetRequired<
			SelectFieldProps<T, Multiple, DisableClearable, FreeSolo>,
			'name'
		>
	> {}

export const LegacyFormSelectFieldPrimitive = <
	T,
	Multiple extends boolean = false,
	DisableClearable extends boolean = false,
	FreeSolo extends boolean = false,
>(
	{
		error,
		form,
		formFieldOptions,
		onChange,
		...props
	}: LegacyFormSelectFieldProps<T, Multiple, DisableClearable, FreeSolo>,
	ref: any,
) => {
	const inputRef = useRef<HTMLInputElement>(null);

	const _error = typeof error === 'string' ? error : error?.message;

	useErrorFocus({
		error: _error,
		inputRef,
	});

	useFormInput(props.name, props.value, form, {
		required: props.required,
		...formFieldOptions,
	});

	const handleChange = useConstCallback(
		splitEventHandler(onChange, (event, value, reason, details) => {
			form?.setValue(props.name, value);
		}),
	);

	return (
		<SelectField<T, Multiple, DisableClearable, FreeSolo>
			ref={ref}
			error={_error}
			{...props}
			inputRef={combineRefs(inputRef, props.inputRef)}
			onChange={handleChange}
		/>
	);
};

export const LegacyFormSelectField = React.forwardRef(
	LegacyFormSelectFieldPrimitive,
) as <
	T,
	Multiple extends boolean = false,
	DisableClearable extends boolean = false,
	FreeSolo extends boolean = false,
>(
	props: LegacyFormSelectFieldProps<T, Multiple, DisableClearable, FreeSolo>,
) => JSX.Element;

export default LegacyFormSelectField;
